import { Suspense, lazy, useEffect, useState } from "react";
import { localStorageColorSchemeManager, MantineProvider } from "@mantine/core";
import { ModalsProvider } from "@mantine/modals";
import { Notifications } from "@mantine/notifications";
import { Route, Routes } from "react-router-dom";
import { SiteLayout } from "./layout/SiteLayout";
import { theme } from "./theme";
import { NifusionErrorBoundary } from "./common/NifusionErrorBoundary";
import { UsernameClaimModal } from "./common/UsernameClaimModal";
import { AuthProvider } from "./lib/auth";
import { NavGuardProvider } from "./lib/navGuard";

const Home = lazy(() => import("./pages/Home"));
const About = lazy(() => import("./pages/About"));
const Apps = lazy(() => import("./pages/Apps"));
const AppsHost = lazy(() => import("./pages/AppsHost"));
const Profile = lazy(() => import("./pages/Profile"));
const NotFound = lazy(() => import("./pages/NotFound"));

declare global {
  interface Window {
    __nifusionTriggerError?: () => void;
  }
}

function AppContent() {
  const [manualCrashToken, setManualCrashToken] = useState(0);

  useEffect(() => {
    window.__nifusionTriggerError = () => {
      setManualCrashToken((n) => n + 1);
    };
    return () => {
      delete window.__nifusionTriggerError;
    };
  }, []);

  if (manualCrashToken > 0) {
    throw new Error("Insert error details here or something");
  }

  return (
    <Suspense fallback={null}>
      <Routes>
        <Route element={<SiteLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/apps" element={<Apps />} />
          <Route path="/apps/:slug/*" element={<AppsHost />} />
          <Route path="/profile" element={<Profile />} />
          {/* Legacy routes so old /cmd and /niflets links still resolve */}
          <Route path="/cmd" element={<Apps />} />
          <Route path="/cmd/:slug/*" element={<AppsHost />} />
          <Route path="/niflets" element={<Apps />} />
          <Route path="/niflets/:slug/*" element={<AppsHost />} />
          {/* Catch-all 404 */}
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <MantineProvider
      theme={theme}
      colorSchemeManager={localStorageColorSchemeManager({
        key: "nifusion-color-scheme",
      })}
      defaultColorScheme="dark"
    >
      <ModalsProvider>
        <Notifications position="top-right" zIndex={4000} />
        <NifusionErrorBoundary>
          <AuthProvider>
            <NavGuardProvider>
              <AppContent />
              <UsernameClaimModal />
            </NavGuardProvider>
          </AuthProvider>
        </NifusionErrorBoundary>
      </ModalsProvider>
    </MantineProvider>
  );
}
